import { createFeatureSelector, createSelector } from '@ngrx/store';
import { List } from 'immutable';
import { handleActions } from 'redux-actions';
import {
  ActionsEnum,
  FetchFeaturesDataError,
  UpdateFeaturesData,
  UpdateQuotasBannersCanvases,
} from './features-data.actions';
import { BannerCanvasTypesEnum } from './features-data.data';
import { ProvisioningFeatures } from './provisioning-features.model';
import { Banner } from './sub-models/banner.model';
import { BannersCanvases } from './sub-models/banners-canvases.model';
import { Canvas } from './sub-models/canvas.model';
import { Features } from './sub-models/features.model';
import { ProvisioningSubscription } from './sub-models/provisioning-subscription.model';

// feature name
export const FEATURE_NAME = 'feature-data';

// state interface
export interface IState {
  fetchError: Error;
  loaded: boolean;
  subscription: ProvisioningSubscription;
  bannersCanvases: BannersCanvases;
  features: Features;
}

// initial state of store
export const initialState: IState = {
  fetchError: null,
  loaded: false,
  subscription: void 0,
  bannersCanvases: void 0,
  features: void 0,
};

// reducers
const reducerFn = handleActions(
  {
    [ActionsEnum.UPDATE_QUOTAS_BANNERS_CANVASES]: function (
      state: IState,
      { payload }: UpdateQuotasBannersCanvases
    ): IState {
      let { quotas, banners, canvases } = payload;
      let bannersCanvases: BannersCanvases = state.bannersCanvases;

      banners = bannersCanvases.banners
        .filter((banner) => banner.type !== BannerCanvasTypesEnum.quota_reached)
        .concat(banners) as List<Banner>;
      canvases = bannersCanvases.canvases
        .filter((canvas) => canvas.type !== BannerCanvasTypesEnum.quota_reached)
        .concat(canvases) as List<Canvas>;

      return {
        ...state,
        subscription: state.subscription.set(
          ProvisioningSubscription.KEYS.ACCOUNT_QUOTAS,
          quotas
        ) as ProvisioningSubscription,
        bannersCanvases: bannersCanvases
          .set(BannersCanvases.KEYS.BANNERS, banners)
          .set(BannersCanvases.KEYS.CANVASES, canvases) as BannersCanvases,
      };
    },
    [ActionsEnum.FETCH_FEATURES_DATA_SUCCESS]: function (
      state: IState
    ): IState {
      return {
        ...state,
        loaded: true,
      };
    },
    [ActionsEnum.FETCH_FEATURES_DATA_ERROR]: function (
      state: IState,
      { payload }: FetchFeaturesDataError
    ): IState {
      return {
        ...state,
        loaded: true,
        fetchError:
          payload.error || new Error('feature framework data fetch error'),
        subscription: state.subscription || null,
        bannersCanvases: state.bannersCanvases || null,
        features: state.features || null,
      };
    },
    [ActionsEnum.UPDATE_FEATURES_DATA]: function (
      state: IState,
      { payload }: UpdateFeaturesData
    ): IState {
      const featuresData: ProvisioningFeatures = payload.featuresData;

      return {
        ...state,
        subscription: featuresData.subscription,
        bannersCanvases: featuresData.bannersCanvases,
        features: featuresData.features,
      };
    },
  } as any,
  initialState
);

// required for AOT
export function reducer(state: IState = initialState, action: any) {
  return reducerFn(state, action);
}

// selectors
export const getFeaturesData = createFeatureSelector<IState>(FEATURE_NAME);

export const getSubscription = createSelector(
  getFeaturesData,
  (state: IState) => state.subscription
);

export const getCurrentPlan = createSelector(
  getSubscription,
  (subscription: ProvisioningSubscription) =>
    subscription ? subscription.currentPlan : null
);

export const getAccountQuotas = createSelector(
  getSubscription,
  (subscription: ProvisioningSubscription) =>
    subscription ? subscription.accountQuotas : null
);

export const getPurchaseInfo = createSelector(
  getSubscription,
  (subscription: ProvisioningSubscription) =>
    subscription ? subscription.purchaseInfo : null
);

export const getFeatures = createSelector(
  getFeaturesData,
  (state: IState) => state.features
);

export const getFeatureToggles = createSelector(
  getFeatures,
  (features: Features) => (features ? features.toggles : null)
);

export const getBannerCanvases = createSelector(
  getFeaturesData,
  (state: IState) => state.bannersCanvases
);

export const getBanners = createSelector(
  getBannerCanvases,
  (bannersCanvases: BannersCanvases) =>
    bannersCanvases ? bannersCanvases.banners : null
);

export const getCanvases = createSelector(
  getBannerCanvases,
  (bannersCanvases: BannersCanvases) =>
    bannersCanvases ? bannersCanvases.canvases : null
);
